import {
  Catalog_VariantPackGroupItemsExpandedQuery,
  Catalog_VariantPackGroupItemsExpandedQueryVariables,
} from '@/types';
import {
  CatalogGroupItemsLoader,
  useCatalogGroupItemsStore,
} from '@/stores/catalog-group-items-store';
import { NULL_CATEGORY_VALUE } from '@/utils/category';
import { useApolloClient } from '@apollo/client';
import { useCatalogLazyCarouselsEnabled } from './dynamic-config/use-catalog-lazy-carousels-enabled';
import { useEffect } from 'react';
import { useItemQueryVariables } from './use-item-query-variables';
import { useLimitedPerformanceEnabled } from './use-limited-performance';
import { useNewOrderContext } from '@/contexts/new-order-context';
import { useUserContext } from '@/contexts/user-context';
import { VARIANT_PACK_GROUP_ITEMS_EXPANDED_QUERY } from './use-catalog-variant-pack-group-items';

export const useCatalogGroupItemsLoader = () => {
  const client = useApolloClient();
  const { isGuestMode } = useUserContext();
  const { restaurantUUID, supplierUUID } = useNewOrderContext();
  const itemQueryVariables = useItemQueryVariables();
  const limitedPerformanceEnabled = useLimitedPerformanceEnabled();
  const lazyEnabled = useCatalogLazyCarouselsEnabled();

  const itemLimit = limitedPerformanceEnabled ? 5 : 10;
  const locale = itemQueryVariables.locale;
  const includeSalt = !itemQueryVariables.skipSaltApi;

  useEffect(() => {
    if (!restaurantUUID || !supplierUUID) {
      useCatalogGroupItemsStore
        .getState()
        .configure({ loader: null, scopeKey: null });
      return;
    }

    const loader: CatalogGroupItemsLoader = async (groupKeys) => {
      const { data } = await client.query<
        Catalog_VariantPackGroupItemsExpandedQuery,
        Catalog_VariantPackGroupItemsExpandedQueryVariables
      >({
        context: {
          anonymous: isGuestMode,
          noBatch: true,
        },
        fetchPolicy: 'no-cache',
        query: VARIANT_PACK_GROUP_ITEMS_EXPANDED_QUERY,
        variables: {
          includeNullGroupItems: groupKeys.includes(NULL_CATEGORY_VALUE),
          includeSalt,
          itemLimit,
          locale,
          restaurantUUID,
          supplierUUID,
          variantPackGroupUUIDs: groupKeys.filter(
            (key) => key !== NULL_CATEGORY_VALUE,
          ),
        },
      });
      return data?.getSupplierVariantPackGroupItemsExpanded ?? [];
    };

    useCatalogGroupItemsStore.getState().configure({
      loader,
      // Anything the rows depend on - the store is recreated if any change
      scopeKey: [
        restaurantUUID,
        supplierUUID,
        locale,
        includeSalt,
        itemLimit,
        isGuestMode,
        lazyEnabled,
      ].join(':'),
    });
  }, [
    client,
    includeSalt,
    isGuestMode,
    itemLimit,
    lazyEnabled,
    locale,
    restaurantUUID,
    supplierUUID,
  ]);
};
